// ============================================================
// Intigo label PDF generator (A6, 100 x 150 mm).
// Intigo's Partner V3 API does not return a printable label, so we
// generate our own using pdfmake. The barcode encodes the NID.
// ============================================================

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content } from 'pdfmake/interfaces';
import JsBarcode from 'jsbarcode';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).vfs = (pdfFonts as any).vfs;

const MM_TO_PT = 2.83465;
const A6_WIDTH = 100 * MM_TO_PT;
const A6_HEIGHT = 150 * MM_TO_PT;

export interface IntigoLabelInput {
  providerExternalId: string;
  recipientName: string;
  phone: string;
  address: string;
  cityName: string;
  codAmount: number;
  orderRef: string;
  packageSize: number;
}

export interface IntigoLabelBatchInput {
  shipments: IntigoLabelInput[];
  companyName: string;
}

export async function generateIntigoLabelPdf(input: IntigoLabelBatchInput): Promise<Uint8Array> {
  const pages: Content[] = input.shipments.map((s, idx) => buildLabelPage(s, input.companyName, idx > 0));
  const def: TDocumentDefinitions = {
    pageSize: { width: A6_WIDTH, height: A6_HEIGHT },
    pageMargins: [16, 16, 16, 16],
    content: pages,
    defaultStyle: { fontSize: 9 },
  };
  return new Promise<Uint8Array>((resolve, reject) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (pdfMake.createPdf(def) as any).getBuffer((buf: ArrayBuffer | Uint8Array) => {
        resolve(buf instanceof Uint8Array ? buf : new Uint8Array(buf));
      });
    } catch (e) {
      reject(e);
    }
  });
}

function buildLabelPage(s: IntigoLabelInput, companyName: string, pageBreak: boolean): Content {
  const barcodeDataUrl = renderBarcode(s.providerExternalId);

  const sizeLabel = sizeLabelFor(s.packageSize);
  const content: Content = {
    stack: [
      // Header — sender + size badge
      {
        columns: [
          { text: companyName, fontSize: 11, bold: true, alignment: 'left' },
          { text: sizeLabel, fontSize: 10, alignment: 'right', bold: true, color: '#1B2A4A' },
        ],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: A6_WIDTH - 32, y2: 0, lineWidth: 0.5, lineColor: '#999' }], margin: [0, 4, 0, 6] },

      // Recipient
      { text: 'Destinataire', fontSize: 8, color: '#666' },
      { text: s.recipientName, fontSize: 14, bold: true, margin: [0, 1, 0, 0] },
      { text: s.phone, fontSize: 11, margin: [0, 1, 0, 4] },
      { text: s.address, fontSize: 10 },
      { text: s.cityName, fontSize: 10, bold: true, margin: [0, 1, 0, 8] },

      // COD
      ...(s.codAmount > 0
        ? [
            {
              table: {
                widths: ['*'],
                body: [[{ text: `COD : ${formatDt(s.codAmount)}`, alignment: 'center', fontSize: 14, bold: true, fillColor: '#FFF7E0' }]],
              },
              layout: 'noBorders',
              margin: [0, 0, 0, 8],
            } as Content,
          ]
        : []),

      // Barcode
      barcodeDataUrl
        ? { image: barcodeDataUrl, width: A6_WIDTH - 48, alignment: 'center' }
        : { text: s.providerExternalId, fontSize: 14, alignment: 'center', bold: true },
      { text: s.providerExternalId, fontSize: 10, alignment: 'center', margin: [0, 2, 0, 6] },

      // Footer — order ref
      { text: `Réf. interne : ${s.orderRef}`, fontSize: 8, color: '#666', alignment: 'left' },
    ],
  };
  if (pageBreak) (content as { pageBreak?: 'before' }).pageBreak = 'before';
  return content;
}

function renderBarcode(value: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, value, { format: 'CODE128', displayValue: false, height: 60, margin: 0 });
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function sizeLabelFor(size: number): string {
  if (size >= 5) return 'XL';
  if (size === 4) return 'GRAND';
  return 'PETIT';
}

function formatDt(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' DT';
}
